import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { FormField, FormSection, Input } from '../common/FormField';
import Button from '../common/Button';

const MODULES = [
  { key: 'daily_business', label: 'Daily Business' },
  { key: 'stock_check', label: 'Stock Check' },
  { key: 'liter_statement', label: 'Liter Statement' }
];

const PERMISSIONS = [
  { key: 'create', label: 'Create' },
  { key: 'edit', label: 'Update' },
  { key: 'view', label: 'View' },
  { key: 'delete', label: 'Delete' }
];

const getModulePermissions = (moduleKey) => {
  const base = [
    { key: 'create', label: 'Create' },
    { key: 'edit', label: 'Update' },
    { key: 'view', label: 'View' },
    { key: 'delete', label: 'Delete' }
  ];
  if (moduleKey === 'stock_check') {
    return [...base, { key: 'import', label: 'Import Brand' }];
  }
  // permit_invoice_details is now managed in Shop Settings, not here
  return base;
};

const emptyPermissions = () => ({
  daily_business: [],
  stock_check: [],
  liter_statement: []
});

const defaultManagerPermissions = () => ({
  daily_business: ['create', 'edit', 'view', 'delete'],
  stock_check: ['create', 'edit', 'view', 'delete', 'import'],
  liter_statement: ['create', 'edit', 'view', 'delete']
});

const defaultStaffPermissions = () => ({
  daily_business: ['create', 'view'],
  stock_check: ['create', 'view'],
  liter_statement: ['create', 'view']
});

// All possible liter statement categories (keys match DB / backend)
const ALL_CATEGORIES = [
  { key: 'Brandy',   label: 'Brandy' },
  { key: 'Whisky',   label: 'Whisky' },
  { key: 'Rum',      label: 'Rum' },
  { key: 'GinVodka', label: 'Gin/Vodka' },
  { key: 'LAB',      label: 'LAB' },
  { key: 'Wine',     label: 'Wine' },
  { key: 'Beer',     label: 'Beer' },
  { key: 'Total',    label: 'Total' }
];

const DEFAULT_ENABLED_CATEGORIES = ALL_CATEGORIES.map(c => c.key);

const buildDefaultShopSettings = () => ({
  permitInvoiceDetails: false,
  categories: [...DEFAULT_ENABLED_CATEGORIES],
  categoryOrder: [...DEFAULT_ENABLED_CATEGORIES]
});

// Shared by UserModal (edit, in a modal) and CreateUserPage (create, full page).
// Both screens used to reimplement this exact form/validation/submit logic
// independently; this is the single source of truth for it now.
const UserForm = ({ user, onSuccess, onCancel }) => {
  const isEdit = Boolean(user);

  const buildInitialState = () => {
    if (!user) {
      return {
        name: '',
        phone: '',
        shop_name: '',
        shop_code: '',
        roles: [],
        managerPassword: '',
        staffPassword: '',
        permissions: {
          manager: defaultManagerPermissions(),
          staff: defaultStaffPermissions()
        },
        shopSettings: buildDefaultShopSettings()
      };
    }

    const userRoles = Array.isArray(user.roles) ? user.roles.map(r => r.toLowerCase()) : [];
    const initialPermissions = { manager: emptyPermissions(), staff: emptyPermissions() };

    if (user.permissions) {
      ['manager', 'staff'].forEach(role => {
        if (user.permissions[role]) {
          Object.keys(user.permissions[role]).forEach(module => {
            if (initialPermissions[role][module] !== undefined) {
              // Strip permit_invoice_details — managed by shopSettings now
              initialPermissions[role][module] = (user.permissions[role][module] || []).filter(
                p => p !== 'permit_invoice_details'
              );
            }
          });
        }
      });
    } else {
      if (userRoles.includes('manager')) initialPermissions.manager = defaultManagerPermissions();
      if (userRoles.includes('staff')) initialPermissions.staff = defaultStaffPermissions();
    }

    // Derive permitInvoiceDetails from existing liter_statement permissions
    const existingPerms =
      user.permissions?.manager?.liter_statement ||
      user.permissions?.staff?.liter_statement || [];
    const hasInvoicePerm = existingPerms.includes('permit_invoice_details');

    // Build shop settings from existing user data
    let shopSettings;
    if (user.shop_settings) {
      shopSettings = {
        permitInvoiceDetails: hasInvoicePerm || user.shop_settings.permitInvoiceDetails || false,
        categories: user.shop_settings.categories || [...DEFAULT_ENABLED_CATEGORIES],
        categoryOrder: user.shop_settings.categoryOrder || [...DEFAULT_ENABLED_CATEGORIES]
      };
    } else {
      shopSettings = {
        permitInvoiceDetails: hasInvoicePerm,
        categories: [...DEFAULT_ENABLED_CATEGORIES],
        categoryOrder: [...DEFAULT_ENABLED_CATEGORIES]
      };
    }

    return {
      name: user.name || '',
      phone: user.phone || '',
      shop_name: user.shop_name || '',
      shop_code: user.shop_code || '',
      roles: userRoles,
      managerPassword: '',
      staffPassword: '',
      permissions: initialPermissions,
      shopSettings
    };
  };

  const [formData, setFormData] = useState(buildInitialState);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setFormData(buildInitialState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 20);
    setFormData(prev => ({ ...prev, phone: value }));
    if (errors.phone) {
      setErrors(prev => ({ ...prev, phone: undefined }));
    }
  };

  const handleRoleChange = (role) => {
    const isAdding = !formData.roles.includes(role);

    setFormData(prev => {
      const newRoles = isAdding ? [...prev.roles, role] : prev.roles.filter(r => r !== role);
      const newPermissions = {
        manager: { ...prev.permissions.manager },
        staff: { ...prev.permissions.staff }
      };

      if (isAdding) {
        const defaults = role === 'manager' ? defaultManagerPermissions() : defaultStaffPermissions();
        MODULES.forEach(module => {
          if (!newPermissions[role][module.key] || newPermissions[role][module.key].length === 0) {
            newPermissions[role][module.key] = defaults[module.key];
          }
        });
      } else {
        MODULES.forEach(module => {
          newPermissions[role][module.key] = [];
        });
      }

      return { ...prev, roles: newRoles, permissions: newPermissions };
    });

    if (errors.roles) {
      setErrors(prev => ({ ...prev, roles: undefined }));
    }
  };

  const handlePermissionChange = (role, module, permission) => {
    setFormData(prev => {
      const rolePermissions = prev.permissions[role] || emptyPermissions();
      const modulePermissions = [...(rolePermissions[module] || [])];
      const updated = modulePermissions.includes(permission)
        ? modulePermissions.filter(p => p !== permission)
        : [...modulePermissions, permission];

      return {
        ...prev,
        permissions: {
          ...prev.permissions,
          [role]: { ...rolePermissions, [module]: updated }
        }
      };
    });
  };

  // ── Shop Settings handlers ───────────────────────────────────────────────

  const handlePermitInvoiceChange = (checked) => {
    setFormData(prev => ({
      ...prev,
      shopSettings: { ...prev.shopSettings, permitInvoiceDetails: checked }
    }));
  };

  const handleCategoryToggle = (categoryKey) => {
    setFormData(prev => {
      const ss = prev.shopSettings;
      const isEnabled = ss.categories.includes(categoryKey);
      let newCategories, newOrder;
      if (isEnabled) {
        // Disable — remove from both lists
        newCategories = ss.categories.filter(c => c !== categoryKey);
        newOrder = ss.categoryOrder.filter(c => c !== categoryKey);
      } else {
        // Enable — add at end of order
        newCategories = [...ss.categories, categoryKey];
        newOrder = [...ss.categoryOrder, categoryKey];
      }
      return { ...prev, shopSettings: { ...ss, categories: newCategories, categoryOrder: newOrder } };
    });
  };

  const moveCategoryUp = (categoryKey) => {
    setFormData(prev => {
      const order = [...prev.shopSettings.categoryOrder];
      const idx = order.indexOf(categoryKey);
      if (idx <= 0) return prev;
      [order[idx - 1], order[idx]] = [order[idx], order[idx - 1]];
      return { ...prev, shopSettings: { ...prev.shopSettings, categoryOrder: order } };
    });
  };

  const moveCategoryDown = (categoryKey) => {
    setFormData(prev => {
      const order = [...prev.shopSettings.categoryOrder];
      const idx = order.indexOf(categoryKey);
      if (idx < 0 || idx >= order.length - 1) return prev;
      [order[idx], order[idx + 1]] = [order[idx + 1], order[idx]];
      return { ...prev, shopSettings: { ...prev.shopSettings, categoryOrder: order } };
    });
  };

  // ── Validation & Submit ──────────────────────────────────────────────────

  const validate = () => {
    if (!formData.roles || formData.roles.length === 0) {
      setErrors({ roles: 'At least one role is required' });
      return false;
    }
    if (!formData.phone || formData.phone.length < 10) {
      setErrors({ phone: 'Phone number must be at least 10 digits' });
      return false;
    }
    if (!isEdit && (!formData.name || formData.name.trim().length === 0)) {
      setErrors({ name: 'Name is required' });
      return false;
    }

    const passwordRequired = !isEdit;
    if (formData.roles.includes('manager')) {
      const pw = formData.managerPassword;
      if (passwordRequired && (!pw || pw.length < 6)) {
        setErrors({ managerPassword: 'Manager password is required (min 6 characters)' });
        return false;
      }
      if (!passwordRequired && pw && pw.length < 6) {
        setErrors({ managerPassword: 'Manager password must be at least 6 characters' });
        return false;
      }
    }
    if (formData.roles.includes('staff')) {
      const pw = formData.staffPassword;
      if (passwordRequired && (!pw || pw.length < 6)) {
        setErrors({ staffPassword: 'Staff password is required (min 6 characters)' });
        return false;
      }
      if (!passwordRequired && pw && pw.length < 6) {
        setErrors({ staffPassword: 'Staff password must be at least 6 characters' });
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    if (!validate()) return;

    setLoading(true);
    try {
      const data = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        shop_name: formData.shop_name ? formData.shop_name.trim() : '',
        shop_code: formData.shop_code ? formData.shop_code.trim() : '',
        roles: formData.roles,
        permissions: formData.permissions,
        shop_settings: formData.shopSettings,
        ...(formData.roles.includes('manager') && formData.managerPassword && { managerPassword: formData.managerPassword }),
        ...(formData.roles.includes('staff') && formData.staffPassword && { staffPassword: formData.staffPassword })
      };

      const response = isEdit
        ? await api.put(`/users/${user.id}`, data)
        : await api.post('/users', data);

      if (onSuccess) onSuccess(response.data.user);
    } catch (error) {
      console.error('Error saving user:', error);
      if (error.response?.data?.errors) {
        const formErrors = {};
        error.response.data.errors.forEach(err => {
          formErrors[err.param] = err.msg;
        });
        setErrors(formErrors);
      } else if (error.response?.data?.error) {
        setErrors({ general: error.response.data.error });
      } else if (error.response?.status === 401) {
        setErrors({ general: 'Your session has expired. Please log in again.' });
      } else {
        setErrors({ general: error.message || 'Failed to save user. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  // Ordered categories currently enabled (for the order editor)
  const orderedEnabledCategories = formData.shopSettings.categoryOrder.filter(
    k => formData.shopSettings.categories.includes(k)
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <FormSection title="Basic Information" icon="fa-user">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Name" htmlFor="user-name" required error={errors.name}>
            <Input
              id="user-name"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              error={errors.name}
            />
          </FormField>

          <FormField label="Phone Number" htmlFor="user-phone" required error={errors.phone}>
            <Input
              id="user-phone"
              type="text"
              name="phone"
              value={formData.phone}
              onChange={handlePhoneChange}
              required
              minLength={10}
              maxLength={20}
              pattern="[0-9]{10,20}"
              error={errors.phone}
            />
          </FormField>

          <FormField label="Shop Name" htmlFor="user-shop-name">
            <Input id="user-shop-name" type="text" name="shop_name" value={formData.shop_name} onChange={handleChange} />
          </FormField>

          <FormField label="Shop Code" htmlFor="user-shop-code">
            <Input id="user-shop-code" type="text" name="shop_code" value={formData.shop_code} onChange={handleChange} />
          </FormField>
        </div>
      </FormSection>

      <FormSection title={<>Select Roles <span className="text-rose-500">*</span></>} icon="fa-user-tag">
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-3 cursor-pointer px-4 py-3 rounded-xl border-2 border-slate-200 dark:border-slate-600 hover:border-primary/50 dark:hover:border-accent/50 transition-all">
            <input
              type="checkbox"
              checked={formData.roles.includes('manager')}
              onChange={() => handleRoleChange('manager')}
              className="accent-primary w-5 h-5"
            />
            <span className="font-semibold text-slate-700 dark:text-slate-200">Manager</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer px-4 py-3 rounded-xl border-2 border-slate-200 dark:border-slate-600 hover:border-primary/50 dark:hover:border-accent/50 transition-all">
            <input
              type="checkbox"
              checked={formData.roles.includes('staff')}
              onChange={() => handleRoleChange('staff')}
              className="accent-primary w-5 h-5"
            />
            <span className="font-semibold text-slate-700 dark:text-slate-200">Staff</span>
          </label>
        </div>
        {errors.roles && <p className="text-rose-500 text-xs mt-2 flex items-center gap-1"><i className="fas fa-circle-exclamation" aria-hidden="true"></i>{errors.roles}</p>}
      </FormSection>

      {formData.roles.filter(r => r === 'manager' || r === 'staff').length > 0 && (
        <FormSection title="Permissions" icon="fa-key">
          {formData.roles.filter(r => r === 'manager' || r === 'staff').map(role => (
            <div key={role} className="mb-6 last:mb-0">
              <h4 className="text-sm font-bold text-primary dark:text-accent mb-3 capitalize flex items-center gap-2">
                <i className={`fas ${role === 'manager' ? 'fa-user-tie' : 'fa-user'} text-xs`}></i>
                {role} Permissions
              </h4>
              <div className="space-y-3">
                {MODULES.map(module => (
                  <div key={module.key} className="bg-slate-50 dark:bg-slate-900/50 p-4 rounded-xl border border-slate-100 dark:border-slate-700">
                    <h5 className="font-semibold text-sm text-slate-700 dark:text-slate-200 mb-3">{module.label}</h5>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {getModulePermissions(module.key).map(permission => {
                        const isChecked = formData.permissions[role]?.[module.key]?.includes(permission.key) || false;
                        return (
                          <label
                            key={permission.key}
                            className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-white dark:hover:bg-slate-800 transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handlePermissionChange(role, module.key, permission.key)}
                              className="accent-primary w-4 h-4 cursor-pointer"
                            />
                            <span className="text-sm text-slate-700 dark:text-slate-300">{permission.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </FormSection>
      )}

      {/* ── Shop Settings ────────────────────────────────────────────────── */}
      <FormSection title="Shop Settings" icon="fa-store">
        {/* Permit Invoice Details */}
        <div className="mb-5 p-4 rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              id="shop-permit-invoice"
              type="checkbox"
              checked={formData.shopSettings.permitInvoiceDetails}
              onChange={(e) => handlePermitInvoiceChange(e.target.checked)}
              className="accent-primary w-5 h-5 mt-0.5 cursor-pointer"
            />
            <div>
              <span className="font-semibold text-slate-800 dark:text-slate-100 block">
                Permit Invoice Details
              </span>
              <span className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 block">
                Enables the Permit Invoice Details section in Liter Statement for both Manager and Staff users of this store.
              </span>
            </div>
          </label>
        </div>

        {/* Module Category Settings */}
        <div>
          <h4 className="text-sm font-bold text-slate-700 dark:text-slate-200 mb-1 flex items-center gap-2">
            <i className="fas fa-list-check text-primary dark:text-accent text-xs"></i>
            Module Category Settings
          </h4>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
            Select which categories are visible to assigned Manager &amp; Staff users. Use the arrows to set display order.
          </p>

          {/* Category checkboxes */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
            {ALL_CATEGORIES.map(cat => {
              const isEnabled = formData.shopSettings.categories.includes(cat.key);
              return (
                <label
                  key={cat.key}
                  className={`flex items-center gap-2 cursor-pointer p-3 rounded-xl border-2 transition-all ${
                    isEnabled
                      ? 'border-primary/60 bg-primary/5 dark:border-accent/60 dark:bg-accent/5'
                      : 'border-slate-200 dark:border-slate-600 hover:border-primary/30 dark:hover:border-accent/30'
                  }`}
                >
                  <input
                    type="checkbox"
                    id={`cat-${cat.key}`}
                    checked={isEnabled}
                    onChange={() => handleCategoryToggle(cat.key)}
                    className="accent-primary w-4 h-4 cursor-pointer"
                  />
                  <span className={`text-sm font-medium ${
                    isEnabled
                      ? 'text-primary dark:text-accent'
                      : 'text-slate-600 dark:text-slate-400'
                  }`}>
                    {cat.label}
                  </span>
                </label>
              );
            })}
          </div>

          {/* Display Order editor */}
          {orderedEnabledCategories.length > 0 && (
            <div>
              <h5 className="text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <i className="fas fa-sort text-xs"></i>
                Display Order
              </h5>
              <div className="space-y-1.5">
                {orderedEnabledCategories.map((key, idx) => {
                  const cat = ALL_CATEGORIES.find(c => c.key === key);
                  return (
                    <div
                      key={key}
                      className="flex items-center gap-3 p-2.5 rounded-xl bg-slate-50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-700"
                    >
                      <span className="w-6 h-6 rounded-lg bg-primary/10 dark:bg-accent/10 text-primary dark:text-accent text-xs font-bold flex items-center justify-center shrink-0">
                        {idx + 1}
                      </span>
                      <span className="flex-1 text-sm font-medium text-slate-700 dark:text-slate-200">
                        {cat?.label || key}
                      </span>
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => moveCategoryUp(key)}
                          disabled={idx === 0}
                          className="w-7 h-7 rounded-lg bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-500 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                          aria-label={`Move ${cat?.label} up`}
                        >
                          <i className="fas fa-chevron-up text-xs"></i>
                        </button>
                        <button
                          type="button"
                          onClick={() => moveCategoryDown(key)}
                          disabled={idx === orderedEnabledCategories.length - 1}
                          className="w-7 h-7 rounded-lg bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-500 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                          aria-label={`Move ${cat?.label} down`}
                        >
                          <i className="fas fa-chevron-down text-xs"></i>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </FormSection>

      {(formData.roles.includes('manager') || formData.roles.includes('staff')) && (
        <FormSection title="Passwords" icon="fa-lock">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {formData.roles.includes('manager') && (
              <FormField
                label={`Manager Password${isEdit ? ' (leave blank to keep current)' : ''}`}
                htmlFor="user-manager-password"
                required={!isEdit}
                error={errors.managerPassword}
              >
                <Input
                  id="user-manager-password"
                  type="password"
                  name="managerPassword"
                  value={formData.managerPassword}
                  onChange={handleChange}
                  required={!isEdit}
                  minLength={6}
                  autoComplete="new-password"
                  error={errors.managerPassword}
                />
              </FormField>
            )}

            {formData.roles.includes('staff') && (
              <FormField
                label={`Staff Password${isEdit ? ' (leave blank to keep current)' : ''}`}
                htmlFor="user-staff-password"
                required={!isEdit}
                error={errors.staffPassword}
              >
                <Input
                  id="user-staff-password"
                  type="password"
                  name="staffPassword"
                  value={formData.staffPassword}
                  onChange={handleChange}
                  required={!isEdit}
                  minLength={6}
                  autoComplete="new-password"
                  error={errors.staffPassword}
                />
              </FormField>
            )}
          </div>
        </FormSection>
      )}

      {errors.general && (
        <div className="p-4 bg-red-50 dark:bg-red-500/10 border-2 border-red-200 dark:border-red-500/30 rounded-xl" role="alert">
          <p className="text-red-600 dark:text-red-400 text-sm font-medium"><i className="fas fa-exclamation-circle mr-2" aria-hidden="true"></i>{errors.general}</p>
        </div>
      )}

      <div className="flex gap-4 pt-4 border-t border-slate-100 dark:border-slate-700">
        <Button type="button" variant="secondary" fullWidth onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button type="submit" fullWidth loading={loading} icon={isEdit ? 'fa-save' : 'fa-plus'}>
          {loading ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Update User' : 'Create User')}
        </Button>
      </div>
    </form>
  );
};

export default UserForm;
